import { Injectable, inject } from '@angular/core';
import { BaseAppSyncService } from '../base-app-sync.service';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { FacilityApiService } from './facility-api.service';
import { getLocations } from '../../graphql/facilities/get-locations';
import { getStates } from '../../graphql/facilities/get-states';
import { getCounties } from '../../graphql/facilities/get-counties';
import { getTownships } from '../../graphql/facilities/get-townships';
import { deleteLocation } from '../../graphql/facilities/delete-location';
import { createLocation } from '../../graphql/facilities/create-location';
import { updateLocation } from '../../graphql/facilities/update-location';
import { queryForLocations } from '../../graphql/facilities/query-for-locations';
import { LocationBuilderService } from '../builder/location-builder.service';
import {
  County,
  CreateLocationInput,
  Location,
  LocationConnection,
  LocationSortOrQuery,
} from 'src/app/model/locations/location';

@Injectable({
  providedIn: 'root',
})
export class LocationApiService extends BaseAppSyncService {
  protected _facilityApi = inject(FacilityApiService);
  protected _locationBuilder = inject(LocationBuilderService);

  getLocations(): Observable<Location[]> {
    return this.query(
      getLocations,
      {},
      (items: any[]) =>
        items.map((i) => this._locationBuilder.buildLocation(i)),
      'getLocations',
      'cache-first'
    );
  }

  getStates(): Observable<string[]> {
    return this.query(
      getStates,
      {},
      (i: any[]) => i,
      'getStates',
      'cache-first'
    );
  }

  getCounties(): Observable<County[]> {
    return this.query(
      getCounties,
      {},
      (items: any[]) => items.map((i) => this._locationBuilder.buildCounty(i)),
      'getCounties',
      'cache-first'
    );
  }

  getTownships(): Observable<Location[]> {
    return this.query(
      getTownships,
      {},
      (items: any[]) =>
        items.map((i) => this._locationBuilder.buildLocation(i)),
      'getTownships',
      'cache-first'
    );
  }

  deleteLocation(id: string): Observable<boolean> {
    return this.mutate(
      deleteLocation,
      { id },
      (i: any) => i.value,
      'deleteLocation'
    ).pipe(tap(() => this._facilityApi.loadFacilities(true)));
  }

  createLocation(locationInput: CreateLocationInput): Observable<Location> {
    return this.mutate(
      createLocation,
      { input: locationInput },
      (i: any) => this._locationBuilder.buildLocation(i),
      'createLocation'
    ).pipe(tap(() => this._facilityApi.loadFacilities(true)));
  }

  updateLocation(id: string, locationInput: any): Observable<Location> {
    return this.mutate(
      updateLocation,
      { id, input: locationInput },
      (i: any) => this._locationBuilder.buildLocation(i),
      'updateLocation'
    ).pipe(tap(() => this._facilityApi.loadFacilities(true)));
  }

  queryForLocations(
    limit: number,
    page: number,
    query: LocationSortOrQuery,
    sort: LocationSortOrQuery
  ): Observable<LocationConnection> {
    return this.query(
      queryForLocations,
      { limit, page, query, sort },
      (i: any) => this._locationBuilder.buildLocationConnection(i),
      'queryForLocations'
    );
  }
}
